import { localizedStrings } from '@core/strings'
import React, { useCallback, useMemo } from 'react'
import { Button, DropdownButton } from '@common/material'
import Utils from '@core/utils/utils'
import {
  AddCircleOutline,
  ArrowDropDown,
  CalendarTodayOutlined,
  CancelOutlined,
  CheckCircleOutline,
  Download,
  FactCheckOutlined,
  FileCopy,
  InfoOutlined,
  IosShare,
  PauseCircleOutline,
  PersonAddAlt1Outlined,
  PlayCircleOutline,
  Schedule,
  SyncAlt,
} from '@mui/icons-material'
import {
  PermitDetailsActionsType,
  ResumePermitSubmitData,
  SuspendPermitSubmitData,
} from '@modules/permitDetails/types'
import { TransferPermitModal } from '@modules/permitDetails/modals/TransferPermitModal'
import { CancelPermitModal } from '@modules/permitDetails/modals/CancelPermitModal'
import { RequestExtensionModal } from '@modules/permitDetails/modals/RequestExtensionModal'
import { ClosePermitModal } from '@modules/permitDetails/modals/ClosePermitModal'
import { useTheme } from '@mui/material/styles'
import { PageUrls } from '@core/page-urls'
import { PermitListTabs } from '@core/entities/permit/PermitBase'
import { useModal } from '@common/GlobalModal'
import { useRouter } from 'next/router'
import { useNavigationContext } from '@common/hooks'
import { ShareQRCodeModal } from '@common/ShareQRCodeModal'
import { SuspendResumePermitModal } from '@modules/permitDetails/modals/SuspendResumePermitModal'
import Project from '@core/project'
import {
  useClonePermit,
  usePermitPdfDownloadUrl,
  usePermitPeriodicChecklistTemplates,
  useRequestResumePermit,
  useResumePermit,
  useSignOffPermit,
  useSuspendPermit,
} from '@core/react-query/features/permits/permit'
import { useAvailablePermitTemplates } from '@core/react-query/features/templates/permitTemplates'
import { ShortenPermitModal } from '@modules/permitDetails/modals/ShortenPermitModal'
import { Permit } from '@core/entities/permit/Permit'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { useGetProfile } from '@core/react-query/features/profile'
import { BetaPermitModal } from '@modules/permitDetails/modals/BetaPermitModal'
import { useToast } from '@core/toast'
import { PostPermitChecksSubmitModal } from '../modals/PostPermitChecksSubmitModal'
import { BriefingAddParticipantsModal } from '@modules/briefings'
import { SelectChildPermitModal } from '@modules/permitDetails/modals/SelectChildPermitModal'
import { encodeReturnUrl } from 'webapp/utils'
import { useGetChecklistTemplatesByIds } from '@core/react-query/features/templates/checklistTemplates'

const SecondaryActions: React.FC<{ permit: Permit }> = ({ permit }) => {
  const theme = useTheme()
  const router = useRouter()
  const { organisationId, projectId } = useNavigationContext()
  const { showModal, hideModal } = useModal()
  const pdfMutation = usePermitPdfDownloadUrl({
    onError: (e) => toast.error(e.message),
  })
  const clonePermit = useClonePermit()
  const suspendPermit = useSuspendPermit()
  const resumePermit = useResumePermit()
  const requestResumePermit = useRequestResumePermit()
  const signOffPermit = useSignOffPermit()
  const { data: profile } = useGetProfile()

  const { flags } = useFeatureFlags(['beta'])
  const toast = useToast()

  const canSubmitChecklist = permit?.hasPermission('PERIODIC_CHECKLIST_SUBMIT')
  const { data: periodicChecklists, isLoading: isPeriodicChecklistsLoading } =
    usePermitPeriodicChecklistTemplates(
      { permitId: permit.id },
      { enabled: canSubmitChecklist },
    )
  const periodicChecklistTemplates = useGetChecklistTemplatesByIds(
    {
      projectId: permit.project.id,
      templateIds: (periodicChecklists ?? []).map(
        ({ checklistTemplateId }) => checklistTemplateId,
      ),
    },
    { enabled: canSubmitChecklist },
  )

  // Prefetch allowed children for the "Create Child Permit" action. Only
  // fires when this permit is configured as a parent template, so the dropdown
  // opens with templates ready instead of the previous empty/loading state.
  // `findById` enriches the parent permit's `allowedChildTemplates` only when
  // we're viewing it from a child's perspective — looking at the parent
  // directly, its own `currentVersion.allowedChildTemplates` is empty.
  const { data: allowedChildList } = useAvailablePermitTemplates(
    {
      projectId: permit.project.id,
      parentTemplateId: permit.template?.id,
    },
    {
      enabled:
        !!permit.isParentPermit &&
        permit.canRaiseChildBasedOnStatus() &&
        !!permit.template?.id,
    },
  )
  const allowedChildren = allowedChildList?.templates ?? []

  const handleClone = useCallback(() => {
    clonePermit.mutate(
      {
        permitId: permit.id,
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.permitCloned)
          router.push(
            PageUrls.permits(organisationId, {
              tab: PermitListTabs.DRAFTS,
            }),
          )
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }, [organisationId, permit.id, router])

  const handleDownload = useCallback(async () => {
    const pdfDownloadUrl = await pdfMutation.mutateAsync({
      permitId: permit.id,
    })

    const toastId = Utils.GUID()
    toast.progress(localizedStrings.downloadingPermit, {
      duration: 80 * 1000,
      id: toastId,
    })

    const fileGenerated = await Utils.pollForSuccessfulRequest(
      pdfDownloadUrl.urlDownload,
    )

    if (!fileGenerated) {
      toast.error(localizedStrings.permitDownloadFailed, { id: toastId })
    } else {
      window.location.assign(pdfDownloadUrl.urlDownload)
      toast.success(localizedStrings.permitDownloadComplete, { id: toastId })
    }
  }, [permit.id])

  const handleResumePermit = (data: ResumePermitSubmitData) => {
    const canResumePermit = permit?.hasPermission('SUSPENSION_RESUME')
    const action = canResumePermit ? resumePermit : requestResumePermit
    const message = canResumePermit
      ? localizedStrings.permitResumed
      : localizedStrings.resumePermitRequested

    action.mutate(
      {
        permitId: permit.id,
        comment: data.comment,
        attachmentsKeys: data.attachmentsKeys,
      },
      {
        onSuccess: () => {
          hideModal()
          toast.success(message)
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const handleSuspendPermit = (data: SuspendPermitSubmitData) => {
    suspendPermit.mutate(
      {
        permitId: permit.id,
        comment: data.comment,
        attachmentsKeys: data.attachmentsKeys,
        suspensionReasonCategory: data.suspensionReasonCategory,
      },
      {
        onSuccess: () => {
          hideModal()
          toast.success(localizedStrings.permitSuspended)
        },
        onError: (e) => toast.error(e.message),
      },
    )
  }

  const secondaryActionsOptionsMemoised = useMemo(() => {
    const secondaryActionsOptions: PermitDetailsActionsType[] = []

    secondaryActionsOptions.push({
      name: localizedStrings.share,
      icon: <IosShare />,
      dataTest: 'share',
      divider: true,
      onClick: () => {
        const permitUrl = `${Project.appUrl}${PageUrls.projectPermitDetail({
          organisationId,
          projectId: permit.project.id,
          permitId: permit.id,
        })}`

        showModal(
          <ShareQRCodeModal
            title={localizedStrings.sharePermit}
            entityUrl={permitUrl}
            entityLinkLabel={localizedStrings.permitLink}
            entityLinkDescription={localizedStrings.copyLinkToPermitDescription}
            pdfGenerationFailedMessage={
              localizedStrings.permitPDFGenerationFailed
            }
            pdfMutation={pdfMutation}
            pdfMutationParams={{ permitId: permit.id }}
          />,
        )
      },
    })

    const transferAction: PermitDetailsActionsType = {
      name: localizedStrings.transferPermit,
      icon: <SyncAlt />,
      dataTest: 'transfer-permit',
      onClick: () => {
        showModal(<TransferPermitModal permit={permit} />)
      },
    }
    const cancelAction: PermitDetailsActionsType = {
      name: localizedStrings.cancelPermit,
      dataTest: 'cancel-permit',
      icon: <CancelOutlined />,
      onClick: () => {
        showModal(<CancelPermitModal permitId={permit.id} />)
      },
    }
    const downloadAction: PermitDetailsActionsType = {
      name: localizedStrings.downloadPermit,
      icon: <Download />,
      dataTest: 'downloadPermit',
      onClick: () => handleDownload(),
    }
    const cloneAction: PermitDetailsActionsType = {
      name: localizedStrings.clone,
      icon: <FileCopy />,
      dataTest: 'clone-permit',
      onClick: () => handleClone(),
    }
    const requestExtension: PermitDetailsActionsType = {
      name: localizedStrings.requestExtension,
      icon: <CalendarTodayOutlined />,
      dataTest: 'request-extension-permit',
      onClick: () => showModal(<RequestExtensionModal permit={permit} />),
    }
    const closePermit: PermitDetailsActionsType = {
      name: localizedStrings.closePermit,
      icon: <CheckCircleOutline />,
      dataTest: 'close-permit',
      onClick: () => showModal(<ClosePermitModal permit={permit} />),
    }
    const postPermitChecks: PermitDetailsActionsType = {
      name: localizedStrings.closePermit,
      icon: <CheckCircleOutline />,
      dataTest: 'post-permit-checks',
      disabled: permit.template.requireGeolocationForPostPermitChecks,
      tooltipTitle: permit.template.requireGeolocationForPostPermitChecks
        ? localizedStrings.postPermitChecksGeolocationWebTooltip
        : undefined,
      onClick: () => {
        if (
          !permit?.template?.currentVersion.postPermitChecksQuestions?.questions
            ?.length
        ) {
          return showModal(
            <PostPermitChecksSubmitModal
              permitId={permit.id}
              templateId={permit.template.id}
              disclaimerText={permit.template.requesterConfirmationText}
            />,
          )
        }

        if (projectId) {
          router.push(
            PageUrls.projectPermitSignoffCheck({
              organisationId,
              projectId,
              permitId: permit.id,
            }),
          )
        } else {
          router.push(
            PageUrls.permitSignoffCheck({
              organisationId,
              permitId: permit.id,
            }),
          )
        }
      },
    }
    const suspendPermit: PermitDetailsActionsType = {
      name: localizedStrings.suspendPermit,
      icon: <PauseCircleOutline />,
      dataTest: 'suspend-permit',
      onClick: () =>
        showModal(
          <SuspendResumePermitModal
            permit={permit}
            onSubmit={handleSuspendPermit}
          />,
        ),
    }
    const resumePermit: PermitDetailsActionsType = {
      name: localizedStrings.resumePermit,
      icon: <PlayCircleOutline />,
      dataTest: 'resume-permit',
      onClick: () =>
        showModal(
          <SuspendResumePermitModal
            permit={permit}
            onSubmit={handleResumePermit}
          />,
        ),
    }

    const shortenPermit: PermitDetailsActionsType = {
      name: localizedStrings.shortenPermit,
      icon: <Schedule />,
      dataTest: 'shorten-permit',
      onClick: () => showModal(<ShortenPermitModal permit={permit} />),
    }

    const addParticipants: PermitDetailsActionsType = {
      name: localizedStrings.addParticipants,
      icon: <PersonAddAlt1Outlined />,
      dataTest: 'add-participants',
      onClick: () => {
        showModal(
          <BriefingAddParticipantsModal
            entityId={permit.id}
            entityType='PERMIT'
          />,
        )
      },
    }

    const submitChecklist: PermitDetailsActionsType = {
      name: localizedStrings.submitChecklist,
      icon: <FactCheckOutlined />,
      dataTest: 'submit-checklist',
      loading: isPeriodicChecklistsLoading,
      subOptions: (periodicChecklists ?? []).map((periodicChecklist, index) => {
        const { data: checklistTemplate, isLoading } =
          periodicChecklistTemplates[index]
        const requiresDeviceLocation =
          !!checklistTemplate?.requireGeolocationForChecklist &&
          !!checklistTemplate?.geoLocationInputTypes?.includes(
            'DEVICE_LOCATION',
          )

        return {
          title: periodicChecklist.name,
          'data-test': `submit-checklist-${periodicChecklist.checklistTemplateId}`,
          disabled: isLoading || requiresDeviceLocation,
          tooltipTitle: requiresDeviceLocation
            ? localizedStrings.completeAtPointOfWorks
            : undefined,
          onSelect: () => {
            const returnUrl = encodeReturnUrl({
              pathname: router.pathname,
              query: router.query,
            })
            router.push({
              pathname: PageUrls.newChecklistProject({
                organisationId: permit.organisationId,
                projectId: permit.project.id,
              }),
              query: {
                permitId: permit.id,
                templateId: periodicChecklist.checklistTemplateId,
                returnUrl,
              },
            })
          },
        }
      }),
    }

    const createChildPermit: PermitDetailsActionsType = {
      name: localizedStrings.createChildPermit,
      icon: <AddCircleOutline />,
      dataTest: 'create-child-permit',
      onClick: () => {
        showModal(
          <SelectChildPermitModal
            parentPermit={permit}
            projectId={permit.project.id}
            allowedChildren={allowedChildren}
          />,
        )
      },
    }

    const beta: PermitDetailsActionsType = {
      name: 'Beta',
      icon: <InfoOutlined />,
      dataTest: 'beta',
      onClick: () => {
        showModal(<BetaPermitModal permit={permit} />)
      },
    }

    const isPermitSuspended = permit.status === 'SUSPENDED'
    const showSuspendPermit =
      permit?.hasPermission('SUSPEND') &&
      ['ISSUED', 'OPEN', 'EXTENSION_REQUESTED'].includes(permit.status)
    const showResumePermit =
      isPermitSuspended &&
      (permit?.hasPermission('REQUEST_SUSPENSION_RESUME') ||
        permit?.hasPermission('SUSPENSION_RESUME'))
    const showPostPermitChecks =
      !permit.countdownExpiry &&
      permit?.hasPermission('SUBMIT_POST_PERMIT_CHECKS') &&
      !isPermitSuspended
    const showClosePermit =
      permit?.hasPermission('DECLARE_WORK_COMPLETE') && !isPermitSuspended
    const showRequestExtension =
      permit?.hasPermission('REQUEST_EXTENSION') && !isPermitSuspended
    const showTransferAction =
      permit?.hasPermission('INITIATE_TRANSFER') && !isPermitSuspended

    const showCreateChildPermit =
      permit.canRaiseChildBasedOnStatus() && !!permit.isParentPermit

    showCreateChildPermit && secondaryActionsOptions.push(createChildPermit)

    permit?.hasPermission('BRIEF_PARTICIPANTS') &&
      secondaryActionsOptions.push(addParticipants)
    showPostPermitChecks && secondaryActionsOptions.push(postPermitChecks)
    showClosePermit && secondaryActionsOptions.push(closePermit)
    showRequestExtension && secondaryActionsOptions.push(requestExtension)
    showTransferAction && secondaryActionsOptions.push(transferAction)
    canSubmitChecklist && secondaryActionsOptions.push(submitChecklist)

    if (permit?.hasPermission('CANCEL')) {
      secondaryActionsOptions.push(cancelAction)
    }

    secondaryActionsOptions.push(downloadAction)
    permit?.hasPermission('CLONE') && secondaryActionsOptions.push(cloneAction)
    showSuspendPermit && secondaryActionsOptions.push(suspendPermit)
    showResumePermit && secondaryActionsOptions.push(resumePermit)
    permit?.hasPermission('SHORTEN') &&
      secondaryActionsOptions.push(shortenPermit)
    flags.beta && secondaryActionsOptions.push(beta)

    return secondaryActionsOptions
  }, [
    permit.template.requireGeolocationForPostPermitChecks,
    permit.countdownExpiry,
    showModal,
    permit,
    handleDownload,
    handleClone,
    organisationId,
    pdfMutation,
    router,
    canSubmitChecklist,
    periodicChecklists,
    periodicChecklistTemplates,
    isPeriodicChecklistsLoading,
  ])

  return (
    <DropdownButton
      label={localizedStrings.actions}
      options={secondaryActionsOptionsMemoised.map((secondaryOptions) => ({
        title: secondaryOptions.name,
        value: secondaryOptions.name,
        'data-test': secondaryOptions.dataTest,
        onSelect: secondaryOptions.onClick,
        disabled: secondaryOptions.disabled,
        tooltipTitle: secondaryOptions.tooltipTitle,
        icon: secondaryOptions.icon,
        divider: secondaryOptions.divider,
        loading: secondaryOptions.loading,
        subOptions: secondaryOptions.subOptions,
      }))}
      renderButton={(onClick) => (
        <Button
          variant='outlined'
          data-test='secondary-permit-actions'
          endIcon={<ArrowDropDown />}
          onClick={onClick}
          sx={{
            fontSize: 16,
            background: theme.palette.legacy.white[10],
            ml: 1,
          }}
        >
          {localizedStrings.actions}
        </Button>
      )}
    />
  )
}

export default SecondaryActions
